import { For, Show, createMemo, type Component } from 'solid-js';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Button } from '~ui/button';
import { Input } from '~ui/input';
import { CrossIcon, SearchIcon } from '~ui/icons';
import { Text } from '~ui/text';
import { Table, type Column } from '~ui/table';
import { NA, formatCurrency, formatNumber, formatPercent, roundLargeNumber, todayISO } from '~/lib/format';
import { COVERED, annualDistribution, assets, distributionIsTrailingTaxYear, instrumentYield, managementFee, mandateOf, price, returnOfCapitalShare, riskRating, type UniverseEntry } from '~data';
import { resolved } from '~/stores/portfolio-store';
import { clearScreen, isScreened, preset, query, rootSymbol, screened, setPreset, setQuery, setSelectedSymbol } from '~/stores/explore-store';
import { PRESETS, presetCount } from '~/data/strategies';
import HeldMark from '~/features/held-mark';

const ASOF = todayISO();

// Risk is a phrase, and the phrases do not sort into risk order alphabetically —
// "Low to Medium" lands before "Low", and "High" before either. Ranked here so
// the column sorts by how risky it says the fund is.
const RISK_ORDER = ['Low', 'Low to Medium', 'Medium', 'Medium to High', 'High'];

const riskRank = (symbol: string): number | null => {
    const rating = riskRating(symbol);
    if (rating == null) return null;
    const at = RISK_ORDER.indexOf(rating);
    // An unrecognised rating sorts after every known one rather than silently
    // taking the safest slot.
    return at === -1 ? RISK_ORDER.length : at;
};

const heldUnits = (symbol: string): number =>
    resolved()
        .filter((r) => r.symbol === symbol)
        .reduce((sum, r) => sum + r.holding.units, 0);

const COLUMNS: Column<UniverseEntry>[] = [
    // The mark rides the symbol rather than getting a column. Held already has
    // one, and it carries the units — a number you read once you have found the
    // row. This is what makes you find it: sorted by yield or by fee, the symbol
    // column is what the eye runs down, and "mine" is the one fact about a row
    // that is not in the table at all.
    {
        id: 'symbol',
        header: 'Symbol',
        size: 0.7,
        sortBy: (i) => i.symbol,
        cell: (i) => (
            <span class='flex min-w-0 items-center gap-1'>
                <Text weight='medium'>{i().symbol}</Text>
                <HeldMark symbol={i().symbol} />
            </span>
        ),
    },
    { id: 'sector', header: 'Sector', size: 1.1, sortBy: (i) => mandateOf(i.symbol), cell: (i) => <Text class='truncate'>{mandateOf(i().symbol) ?? NA}</Text> },
    { id: 'price', header: 'Price', align: 'right', size: 0.7, sortBy: (i) => price(i.symbol), cell: (i) => formatCurrency(price(i().symbol)) },
    {
        id: 'dist',
        header: 'Dist / yr',
        align: 'right',
        size: 0.9,
        sortBy: (i) => annualDistribution(i.symbol, ASOF),
        // Muted when it came from the tax table rather than from payments — a
        // real figure, but for last year rather than this one.
        cell: (i) => <Text tone={distributionIsTrailingTaxYear(i().symbol, ASOF) ? 'muted' : undefined}>{formatCurrency(annualDistribution(i().symbol, ASOF))}</Text>,
    },
    { id: 'yield', header: 'Yield', align: 'right', size: 0.7, sortBy: (i) => instrumentYield(i.symbol, ASOF), cell: (i) => formatPercent(instrumentYield(i().symbol, ASOF)) },
    // Return of capital is not taxed when received; it reduces adjusted cost
    // base instead. A high share is much of the point of these funds.
    { id: 'roc', header: 'RoC', align: 'right', size: 0.6, sortBy: (i) => returnOfCapitalShare(i.symbol), cell: (i) => formatPercent(returnOfCapitalShare(i().symbol), 0) },
    { id: 'fee', header: 'Fee', align: 'right', size: 0.6, sortBy: (i) => managementFee(i.symbol), cell: (i) => formatPercent(managementFee(i().symbol)) },
    { id: 'risk', header: 'Risk', size: 0.9, sortBy: (i) => riskRank(i.symbol), cell: (i) => <Text class='truncate'>{riskRating(i().symbol) ?? NA}</Text> },
    { id: 'assets', header: 'Assets', align: 'right', size: 0.8, sortBy: (i) => assets(i.symbol), cell: (i) => (assets(i().symbol) == null ? NA : `$${roundLargeNumber(assets(i().symbol))}`) },
    { id: 'held', header: 'Held', align: 'right', size: 0.7, sortBy: (i) => heldUnits(i.symbol), cell: (i) => <HeldUnits symbol={i().symbol} /> },
];

// Units of this instrument held anywhere in the active portfolio — the join
// back from the pool to the book, so the pane doubles as a "what do I own"
// view rather than a static reference list.
const HeldUnits: Component<{ symbol: string }> = (props) => {
    // The same helper the column sorts by, so the number shown and the number
    // ordered on cannot come apart.
    const units = createMemo(() => heldUnits(props.symbol));
    return <Text tone={units() > 0 ? undefined : 'muted'}>{units() > 0 ? formatNumber(units(), { maximumFractionDigits: 0 }) : '—'}</Text>;
};

// The toolbar earns its row now. It carried a pane title and a coverage note
// that never changed, and was removed for it; what is there instead is the two
// controls that change what the table shows.

// Lit chips use the same fill every toggle in the app does — the one the Select
// menu marks its chosen option with — so "this one is on" reads the same
// everywhere. Trailing `!` because Button's own hover:bg-muted is emitted later
// in the sheet and would otherwise win.
const Preset: Component<{ id: (typeof PRESETS)[number]['id']; label: string }> = (props) => {
    const count = createMemo(() => presetCount(props.id));
    return (
        <Button
            size='fill'
            class={preset() === props.id ? 'bg-muted-foreground/20 border-b-3 border-foreground hover:bg-muted-foreground/20!' : ''}
            onClick={() => setPreset(props.id)}>
            {props.label}
            {/* The count is the honest part. Every fund in the pool today writes
                calls, so that chip narrows nothing — better to say 18 of 18 on
                the button than to let someone click it and wonder why the table
                did not move. The chips separate as coverage widens. */}
            <Text tone='muted'>{formatNumber(count())}</Text>
        </Button>
    );
};

const Instruments: Component = () => {
    const rows = createMemo(() => screened());

    return (
        <Panel>
            <Toolbar>
                <ToolbarSection class='min-w-0 flex-1 gap-1'>
                    {/* Icon inside the box rather than beside it, so the field
                        reads as one control at this row height. */}
                    <label class='flex h-full min-w-0 max-w-56 flex-1 items-center gap-1 rounded px-1.5 hover:bg-muted focus-within:bg-muted'>
                        <span class='shrink-0 text-muted-foreground'>
                            <SearchIcon />
                        </span>
                        {/* Trailing `!` on the padding: Input carries py-1,
                            which fills an h-7 row to the pixel and leaves the
                            field no room to breathe against the label's fill.
                            A plain py-0 would lose on source order. */}
                        <Input bare value={query()} placeholder='Search funds' class='min-w-0 py-0!' onInput={(e) => setQuery(e.currentTarget.value)} />
                    </label>

                    <For each={PRESETS}>{(p) => <Preset id={p.id} label={p.label} />}</For>
                </ToolbarSection>

                <ToolbarSection class='gap-1'>
                    {/* Only while something is on — the full count is already
                        the number of rows on screen. */}
                    <Show when={isScreened()}>
                        <Text tone='muted' class='whitespace-nowrap pl-2'>
                            {formatNumber(rows().length)} of {formatNumber(COVERED.length)}
                        </Text>
                        <Button size='fill' icon={<CrossIcon />} title='Clear filters' onClick={clearScreen} />
                    </Show>
                </ToolbarSection>
            </Toolbar>

            {/* isSelected marks the row the trail started from, not the fund on
                screen: drilling into HYLD's holdings lands on funds that are not
                rows here, and the table should still say where you came from
                rather than going blank. */}
            <Table
                columns={COLUMNS}
                rows={rows()}
                empty={isScreened() ? 'No fund matches' : 'Pool is empty'}
                onRowClick={(e) => setSelectedSymbol(e.symbol)}
                isSelected={(e) => e.symbol === rootSymbol()}
            />
        </Panel>
    );
};

export default Instruments;
